/**
 * /blog/:slug — Single blog post detail page (Jewel/Luxury theme).
 * Renders the LLM-generated Markdown body with `react-markdown`.
 * Sets <title> + meta tags dynamically for SEO.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowRight, Calendar, Tag, Loader2, ArrowLeft, Bot, List, X as XIcon } from "lucide-react";
import { StickyMobileTrialBar } from "./Landing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("pt-BR", {
      day: "2-digit", month: "long", year: "numeric",
    });
  } catch { return ""; }
}

function slugify(text) {
  return String(text).toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim().replace(/\s+/g, "-").slice(0, 80);
}

/**
 * Iter272 · Extract h2/h3 headings from Markdown for TOC drawer.
 * Skips headings inside fenced code blocks.
 */
function extractHeadings(md) {
  if (!md) return [];
  const lines = md.split(/\r?\n/);
  const out = [];
  let inFence = false;
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (/^```/.test(line.trim())) { inFence = !inFence; continue; }
    if (inFence) continue;
    const m = /^(#{2,3})\s+(.+)$/.exec(line);
    if (!m) continue;
    const level = m[1].length;
    const text = m[2].replace(/\*+/g, "").trim();
    out.push({ level, text, id: slugify(text) });
  }
  return out;
}

function setMeta(name, content) {
  if (!content) return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tocOpen, setTocOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    axios.get(`${API}/blog/posts/${slug}`)
      .then(({ data }) => {
        if (!alive) return;
        const p = data.post;
        setPost(p);
        document.title = `${p.title} · AgenteLead Pro Blog`;
        setMeta("description", p.meta_description || "");
        if (p.keywords?.length) setMeta("keywords", p.keywords.join(", "));
      })
      .catch((e) => { if (alive) setError(e?.response?.status === 404 ? "Artigo não encontrado." : (e?.response?.data?.detail || e.message)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slug]);

  const headings = useMemo(() => extractHeadings(post?.body_md || ""), [post?.body_md]);

  // ReactMarkdown custom renderers — inject slug IDs so the TOC anchors work.
  const mdComponents = useMemo(() => ({
    h2: ({ children, ...props }) => {
      const text = String(children?.[0] || "").trim();
      return <h2 id={slugify(text)} {...props}>{children}</h2>;
    },
    h3: ({ children, ...props }) => {
      const text = String(children?.[0] || "").trim();
      return <h3 id={slugify(text)} {...props}>{children}</h3>;
    },
  }), []);

  const scrollToHeading = (id) => {
    setTocOpen(false);
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <div data-testid="blog-post-page" className="min-h-screen pb-[68px] lg:pb-0" style={{ background: "#0F172A", color: "#F5F1E8" }}>
      <header className="border-b sticky top-0 z-30 backdrop-blur-xl" style={{ borderColor: "rgba(132,204,22,0.18)", background: "rgba(15,8,32,0.85)" }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3.5 sm:py-5 flex items-center justify-between gap-3">
          <Link to="/blog" className="text-white/55 hover:text-white text-[11.5px] sm:text-[12px] uppercase tracking-[0.18em] sm:tracking-[0.2em] font-bold inline-flex items-center gap-2"
            data-testid="blog-post-back">
            <ArrowLeft className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Todos os artigos</span>
            <span className="sm:hidden">Voltar</span>
          </Link>
          <div className="flex items-center gap-2">
            {headings.length > 1 && (
              <button
                type="button"
                onClick={() => setTocOpen(true)}
                data-testid="blog-post-toc-button"
                className="lg:hidden inline-flex items-center gap-1.5 h-9 px-3 rounded-full border text-[11.5px] font-bold uppercase tracking-wider"
                style={{ borderColor: "rgba(132,204,22,0.28)", color: "#84CC16", background: "rgba(132,204,22,0.08)" }}>
                <List className="w-3.5 h-3.5" /> Índice
              </button>
            )}
            <Link to="/" data-testid="blog-post-home"
              className="text-[#84CC16] hover:text-white text-[11.5px] sm:text-[12px] uppercase tracking-[0.18em] sm:tracking-[0.2em] font-bold">
              AgenteLead.Pro
            </Link>
          </div>
        </div>
      </header>

      {loading && (
        <div className="max-w-3xl mx-auto px-6 py-24 text-white/60 flex items-center gap-3" data-testid="blog-post-loading">
          <Loader2 className="w-5 h-5 animate-spin" /> Carregando artigo…
        </div>
      )}

      {error && !loading && (
        <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="blog-post-error">
          <h1 className="text-3xl font-display font-extrabold text-white mb-4">404</h1>
          <p className="text-white/60 mb-6">{error}</p>
          <Link to="/blog" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md font-bold text-[13px]"
            style={{ background: "#84CC16", color: "#0F172A" }}>
            <ArrowLeft className="w-4 h-4" /> Voltar ao Blog
          </Link>
        </div>
      )}

      {post && !loading && !error && (
        <article className="max-w-3xl mx-auto px-5 sm:px-6 py-10 sm:py-12 lg:py-16">
          <div className="text-[10.5px] sm:text-[11px] uppercase tracking-[0.2em] sm:tracking-[0.25em] font-bold text-[#84CC16] mb-4 sm:mb-5 flex items-center gap-2 sm:gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1.5"><Tag className="w-3 h-3" /> {post.niche || "Insights"}</span>
            <span className="text-white/30">•</span>
            <span className="inline-flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {formatDate(post.published_at)}</span>
            {post.model && (<>
              <span className="text-white/30 hidden sm:inline">•</span>
              <span className="hidden sm:inline-flex items-center gap-1.5 text-white/40 normal-case tracking-normal"><Bot className="w-3 h-3" /> {post.model}</span>
            </>)}
          </div>

          <h1 className="font-display text-[30px] leading-[1.12] sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-5 sm:mb-6 sm:leading-[1.1] text-white"
            data-testid="blog-post-title">
            {post.title}
          </h1>

          {post.meta_description && (
            <p className="text-[16px] sm:text-base lg:text-lg text-white/75 leading-relaxed mb-8 sm:mb-10 pb-8 sm:pb-10 border-b"
              style={{ borderColor: "rgba(132,204,22,0.18)" }}>
              {post.meta_description}
            </p>
          )}

          {/* Markdown body — Medium/Notion-tuned prose */}
          <div className="blog-prose" data-testid="blog-post-body">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{post.body_md}</ReactMarkdown>
          </div>

          {post.keywords?.length > 0 && (
            <div className="mt-10 sm:mt-12 pt-6 sm:pt-8 border-t flex flex-wrap gap-2"
              style={{ borderColor: "rgba(132,204,22,0.18)" }}>
              {post.keywords.map((k) => (
                <span key={k} className="text-[11px] uppercase tracking-wide font-bold px-3 py-1 rounded-full"
                  style={{ background: "rgba(132,204,22,0.12)", color: "#84CC16", border: "1px solid rgba(132,204,22,0.3)" }}>
                  {k}
                </span>
              ))}
            </div>
          )}

          {/* CTA */}
          <div className="mt-12 sm:mt-16 rounded-2xl p-7 sm:p-10 text-center"
            style={{ background: "linear-gradient(135deg, rgba(132,204,22,0.18), rgba(15,8,32,0.6))", border: "1px solid rgba(132,204,22,0.3)" }}>
            <h3 className="text-[22px] sm:text-2xl font-display font-extrabold text-white mb-3 leading-tight">
              Não perca mais tempo gerenciando dados manuais.
            </h3>
            <p className="text-white/70 mb-6 sm:mb-7 text-[14px] sm:text-base">14 dias grátis · sem cartão de crédito</p>
            <Link to="/signup" data-testid="blog-post-cta"
              className="inline-flex items-center gap-2 px-6 sm:px-7 py-3 rounded-md font-extrabold uppercase tracking-tight text-[13px]"
              style={{ background: "#84CC16", color: "#0F172A", boxShadow: "0 8px 30px -10px rgba(132,204,22,0.55)" }}>
              Automatize sua prospecção <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </article>
      )}

      {/* Iter272 · TOC bottom-sheet drawer (mobile) */}
      {tocOpen && (
        <>
          <div className="lg:hidden fixed inset-0 z-40"
               style={{ background: "rgba(2,6,23,0.7)", backdropFilter: "blur(4px)" }}
               onClick={() => setTocOpen(false)} />
          <aside data-testid="blog-post-toc-drawer"
            className="lg:hidden fixed bottom-0 left-0 right-0 z-50 rounded-t-2xl border-t shadow-2xl max-h-[70vh] flex flex-col"
            style={{ background: "rgba(15,8,32,0.98)", borderColor: "rgba(132,204,22,0.25)" }}>
            <div className="flex items-center justify-between px-5 py-3.5 border-b"
                 style={{ borderColor: "rgba(132,204,22,0.15)" }}>
              <div className="text-[13px] font-black uppercase tracking-widest" style={{ color: "#84CC16" }}>
                <List className="inline w-3.5 h-3.5 mr-1.5" /> Índice do artigo
              </div>
              <button onClick={() => setTocOpen(false)}
                      data-testid="blog-post-toc-close"
                      aria-label="Fechar índice"
                      className="w-10 h-10 -mr-2 rounded-full hover:bg-white/10 text-slate-300 inline-flex items-center justify-center">
                <XIcon className="w-5 h-5" />
              </button>
            </div>
            <nav className="overflow-y-auto px-5 py-3">
              {headings.map((h, i) => (
                <button key={`${h.id}-${i}`} type="button"
                  onClick={() => scrollToHeading(h.id)}
                  data-testid={`blog-post-toc-item-${i}`}
                  className="w-full text-left py-3 text-[14.5px] font-semibold border-b last:border-b-0 flex items-start gap-2"
                  style={{ color: "rgba(255,255,255,0.9)", borderColor: "rgba(132,204,22,0.10)", paddingLeft: h.level === 3 ? 18 : 0 }}>
                  <span className="mt-0.5 shrink-0" style={{ color: h.level === 2 ? "#84CC16" : "rgba(132,204,22,0.6)" }}>
                    {h.level === 2 ? "§" : "›"}
                  </span>
                  <span className="flex-1">{h.text}</span>
                </button>
              ))}
              {headings.length === 0 && (
                <div className="py-6 text-center text-white/50 text-[13px]">
                  Nenhum tópico detectado neste artigo.
                </div>
              )}
            </nav>
          </aside>
        </>
      )}

      <StickyMobileTrialBar lang="pt" variant="blog" />

      <style>{`
        .blog-prose { color: rgba(245,241,232,0.94); font-size: 17.5px; line-height: 1.85; letter-spacing: -0.003em; }
        @media (min-width: 768px) { .blog-prose { font-size: 18px; line-height: 1.8; } }
        .blog-prose h1, .blog-prose h2, .blog-prose h3, .blog-prose h4 {
          color: #FFFFFF; font-family: var(--font-display, 'Playfair Display', serif);
          font-weight: 800; letter-spacing: -0.01em; margin-top: 1.8em; margin-bottom: 0.55em; line-height: 1.22; scroll-margin-top: 80px;
        }
        .blog-prose h2 { font-size: 1.65rem; border-left: 3px solid #84CC16; padding-left: 12px; }
        @media (min-width: 768px) { .blog-prose h2 { font-size: 1.75rem; padding-left: 14px; } }
        .blog-prose h3 { font-size: 1.25rem; color: #84CC16; }
        @media (min-width: 768px) { .blog-prose h3 { font-size: 1.3rem; } }
        .blog-prose p { margin: 1.15em 0; }
        .blog-prose a { color: #84CC16; text-decoration: underline; text-underline-offset: 3px; }
        .blog-prose a:hover { color: #FFFFFF; }
        .blog-prose strong { color: #FFFFFF; font-weight: 700; }
        .blog-prose ul, .blog-prose ol { margin: 1.15em 0; padding-left: 1.4em; }
        .blog-prose li { margin: 0.5em 0; }
        .blog-prose ul li::marker { color: #84CC16; }
        .blog-prose blockquote {
          border-left: 3px solid #84CC16; padding-left: 16px; margin: 1.6em 0;
          color: rgba(245,241,232,0.72); font-style: italic;
        }
        .blog-prose code { background: rgba(132,204,22,0.12); padding: 2px 6px; border-radius: 4px;
          font-size: 0.9em; color: #84CC16; }
        .blog-prose pre { background: rgba(0,0,0,0.4); padding: 14px; border-radius: 8px;
          overflow-x: auto; border: 1px solid rgba(132,204,22,0.18); font-size: 14px; }
        .blog-prose pre code { background: transparent; padding: 0; color: rgba(245,241,232,0.95); }
        .blog-prose hr { border: 0; border-top: 1px solid rgba(132,204,22,0.2); margin: 2.5em 0; }
        .blog-prose table { border-collapse: collapse; margin: 1.5em 0; width: 100%; font-size: 15px; }
        .blog-prose th, .blog-prose td { border: 1px solid rgba(132,204,22,0.2); padding: 10px 12px; text-align: left; }
        .blog-prose th { background: rgba(132,204,22,0.08); color: #84CC16; font-weight: 700; }
      `}</style>
    </div>
  );
}
